"use client";

import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { Button } from "@/shared/ui/button";

import { usePlayerAllTimeReplayMetrics } from "../api/use-replay-metrics";
import type { ReplayMetricKey, ReplayPlayerMetrics } from "../model/types";

/**
 * KAN-727: компактная полоска поведенческих метрик для превью профиля (эпик KAN-719).
 *
 * Превью — это карточка на пол-экрана, в которую заглядывают на секунду. Радар туда не
 * влезает и не читается, поэтому здесь три числа и кнопка на полную страницу. Выбор
 * именно трёх метрик не про «самые важные», а про то, что помещается в одну строку и
 * при этом говорит о РАЗНОМ: лидерство, игра на объекты, дисциплина.
 *
 * ПЕРЦЕНТИЛЕЙ ЗДЕСЬ НЕТ. Метрики за всё время считаются по всем турнирам, а группа
 * сравнения у перцентиля — роль внутри одного турнира. Поэтому показываем абсолютные
 * значения и ничего не обещаем про «выше N% игроков».
 *
 * Если разобранных реплеев нет — компонент не рисует ничего. Пустая полоска с прочерками
 * в превью выглядит как «игрок ничего не делал», хотя правда — «данных нет».
 */

interface BehaviorStripProps {
  userId: string | null | undefined;
  /** Куда ведёт кнопка «подробнее» — страница статистики игрока. */
  statsHref: string;
  onNavigate?: () => void;
}

/** Три метрики для узкой полоски: лидерство, макро, дисциплина. */
const STRIP_KEYS: ReplayMetricKey[] = [
  "shotcalling",
  "objectiveFocus",
  "goldUtilization",
];

function formatValue(key: ReplayMetricKey, value: number): string {
  if (key === "goldUtilization") return `${Math.round(value * 100)}%`;
  if (key === "objectiveFocus") return value.toFixed(2);
  return value >= 10 ? Math.round(value).toString() : value.toFixed(1);
}

export function BehaviorStrip({ userId, statsHref, onNavigate }: BehaviorStripProps) {
  const t = useTranslations("Profile.behaviorMetrics");
  const { data, isLoading, isError } = usePlayerAllTimeReplayMetrics(userId ?? undefined);

  if (isLoading || isError || !data) return null;

  const shown = STRIP_KEYS.map((key) => ({
    key,
    metric: data.metrics.find((m) => m.key === key),
  })).filter((row) => row.metric?.value != null);

  // Ни одной метрики с данными — полоски нет вовсе.
  if (shown.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t("stripTitle")}</span>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {t("gamesCount", { count: data.gamesTotal })}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {shown.map(({ key, metric }) => (
          <div key={key} className="rounded-lg bg-muted/60 px-2 py-1.5 text-center">
            <div className="text-sm font-semibold tabular-nums">
              {formatValue(key, metric!.value!)}
            </div>
            <div className="text-[10px] leading-tight text-muted-foreground truncate">
              {t(`axis.${key}`)}
            </div>
          </div>
        ))}
      </div>

      <Button asChild variant="outline" size="sm" className="w-full">
        <Link href={statsHref} onClick={onNavigate}>
          {t("openFullStats")}
        </Link>
      </Button>
    </div>
  );
}

/** Экспорт для страницы статистики: там та же выборка, но показывается целиком. */
export type { ReplayPlayerMetrics };
